import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import swal from 'sweetalert';
import { getVendorServices, markCompleteVendorService } from '../../api/vendorServiceApi';
import PackageServiceDetails from './PackageServiceDetails';
import VendorOrdersCalendar from './VendorOrdersCalendar';
import { VendorPayoutLegendVendor } from '../admin/AdminStatusLegend';
import { vendorPayoutStatus, formatVendorShare } from '../../utils/vendorPayoutStatus';
import '../admin/BookingCalendar.scss';

function getServiceDateRaw(service) {
	if (!service) return null;
	const fromExtras = service.extras && typeof service.extras === 'object' && (
		service.extras.start_date || service.extras.pickup_date
	);
	return service.preferred_date || fromExtras || null;
}

function getServiceYearMonth(service) {
	const raw = getServiceDateRaw(service);
	if (!raw) return null;
	return String(raw).slice(0, 7);
}

function getServiceDate(service) {
	const raw = getServiceDateRaw(service);
	return raw ? moment(raw.length === 10 ? raw : raw.slice(0, 10)).format('D MMM YYYY') : '—';
}

function PackageBookings(props) {
	const [services, setServices] = useState([]);
	const [viewDetails, setViewDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [markingId, setMarkingId] = useState(null);

	const vendorId = props.currentUser?.vendor?.idx || props.currentUser?.partner?.idx;

	const fetchServices = useCallback(() => {
		if (!vendorId) {
			setLoading(false);
			setError(true);
			return;
		}
		setLoading(true);
		setError(false);
		getVendorServices({ per_page: 500, sort_by: 'date', sort_order: 'desc' })
			.then((response) => {
				const list = response.data?.vendor_services ?? [];
				setServices(Array.isArray(list) ? list : []);
			})
			.catch(() => { setError(true); setServices([]); })
			.finally(() => setLoading(false));
	}, [vendorId]);

	useEffect(() => { fetchServices(); }, [fetchServices]);

	const handleMarkComplete = (service) => {
		if (!service.idx) return;
		setMarkingId(service.idx);
		markCompleteVendorService(service.idx)
			.then(() => { swal('Done', 'Marked complete. Admin will record your payment.', 'success'); fetchServices(); })
			.catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Failed', 'error'))
			.finally(() => setMarkingId(null));
	};

	if (viewDetails) {
		return (
			<div className="container py-4">
				<button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={() => setViewDetails(null)}>← Back</button>
				<PackageServiceDetails service={viewDetails} />
			</div>
		);
	}

	return (
		<div className="container py-4">
			<Link to="/partner" className="btn btn-outline-secondary btn-sm mb-3">← Back to Dashboard</Link>
			<h2 className="mb-3">My Private Tours</h2>
			<VendorPayoutLegendVendor />
			<p className="text-muted small mb-3">Click a month → mark complete when the tour is done.</p>

			{error && (
				<div className="alert alert-warning">
					Could not load tours. {vendorId && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fetchServices}>Retry</button>}
				</div>
			)}

			{!error && (
				<VendorOrdersCalendar
					items={services}
					getMonthKey={getServiceYearMonth}
					loading={loading}
					emptyMessage="No private tours assigned yet."
					renderTable={(monthItems) => (
						<div className="table-responsive">
							<table className="table table-sm table-hover booking-cal__table">
								<thead>
									<tr>
										<th>Tour</th>
										<th>Date</th>
										<th>Customer</th>
										<th>Your amount</th>
										<th>Status</th>
										<th />
									</tr>
								</thead>
								<tbody>
									{monthItems.map((service) => {
										const status = vendorPayoutStatus(service, { audience: 'vendor' });
										return (
											<tr key={service.idx || service.id}>
												<td><strong>{service.tour_name || service.name || '—'}</strong></td>
												<td>{getServiceDate(service)}</td>
												<td className="small">{service.customer_name || service.customer_email || '—'}</td>
												<td>{formatVendorShare(service)}</td>
												<td>
													<span className={`badge badge-${status.badge}`}>{status.label}</span>
													{status.hint && <div className="small text-muted">{status.hint}</div>}
												</td>
												<td className="text-right text-nowrap">
													<button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={() => setViewDetails(service)}>Details</button>
													{!service.vendor_completed_at && (
														<button type="button" className="btn btn-sm btn-success" onClick={() => handleMarkComplete(service)} disabled={markingId === service.idx}>
															{markingId === service.idx ? '…' : 'Mark complete'}
														</button>
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				/>
			)}
		</div>
	);
}

export default connect(({ userStore }) => ({ currentUser: userStore.currentUser }))(PackageBookings);
